import { openPage } from "@nanostores/router";
import clsx from "clsx";
import { FC, ReactElement, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as z from "zod";

import {
  createEntity,
  Entity,
  EntityClass,
  EntityColorSchema,
  EntityIconToken,
  updateEntity,
} from "../api";
import { zEntityUpsert } from "../api/zod.gen";
import { colorSchemas } from "../config/entities";
import { useServerErrors } from "../hooks/useServerErrors";
import { $isEntityCreationFormOpen } from "../stores/graph/entities";
import { $router } from "../stores/router";
import { validEntityName } from "../utils/regex-definitions";
import { Button } from "./catalyst/Button";
import { ErrorMessage } from "./catalyst/ErrorMessage";
import { Field } from "./catalyst/Field";
import { Label } from "./catalyst/Label";
import { IconSelectField } from "./icons/IconSelectField";
import FormErrorList from "./inputs/FormErrorList";
import InputFieldGroup from "./inputs/InputFieldGroup";
import SelectFieldGroup from "./inputs/SelectFieldGroup";
import { NodePreview } from "./NodePreview";

type EntityValues = z.infer<typeof zEntityUpsert>;

export interface EntityFormContentProps {
  entity?: Entity;
  entityClass: Pick<EntityClass, "id" | "name">;
  isCompact?: boolean;
}

export const EntityFormContent: FC<EntityFormContentProps> = ({
  entity,
  entityClass,
  isCompact = false,
}): ReactElement => {
  const isEditMode: boolean = !!entity;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const entityForm = useForm<EntityValues>({
    defaultValues: {
      colorSchema: EntityColorSchema.AMBER,
      defaultValue: "",
      description: "",
      icon: EntityIconToken.AFFILIATE,
      name: "",
      qualifiedName: "",
      valueRegExpPattern: "",
    },
  });

  const {
    control,
    formState: { dirtyFields, errors },
    register,
    setValue,
    watch,
  } = entityForm;

  const { clearServerErrors, handleServerError, serverErrors } =
    useServerErrors({
      formMethods: entityForm,
    });

  const entityName = watch("name");
  const tenant = "miru";
  const [entityIdPlaceholder, setEntityIdPlaceholder] = useState(
    `entity.${tenant}.entity-class.entity`,
  );

  useEffect(() => {
    const className = entityClass.name;
    const defaultId = `entity.${tenant}.${className}.${entityName}`
      .toLocaleLowerCase()
      .replace(/\s+/g, "_");
    if (!isEditMode && !dirtyFields.qualifiedName && entityName) {
      setValue("qualifiedName", defaultId);
    } else {
      setEntityIdPlaceholder(defaultId);
    }
  }, [entityClass.name, entityName]);

  useEffect(() => {
    if (entity) {
      entityForm.reset({
        ...entity,
      });
    }
  }, [entity, entityForm]);

  useEffect(() => {
    if (window._refreshEntityList) {
      window._refreshEntityList();
    }
  }, []);

  const onSubmit = async (data: EntityValues): Promise<void> => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    clearServerErrors();

    const body = {
      colorSchema: data.colorSchema,
      defaultValue: data.defaultValue,
      description: data.description,
      entityClassId: entityClass.id,
      icon: data.icon,
      name: data.name,
      qualifiedName: data.qualifiedName,
      valueRegExpPattern: data.valueRegExpPattern,
    };

    try {
      if (isEditMode && entity) {
        await updateEntity({
          body,
          path: { entityId: entity.id },
        });
      } else {
        await createEntity({ body });
      }

      toast.success(
        `Entity ${isEditMode ? "updated" : "created"} successfully`,
      );

      if (!isCompact) {
        openPage($router, "entities");
      } else {
        $isEntityCreationFormOpen.set(false);
      }
    } catch (error) {
      console.error("Entity submission error:", error);
      handleServerError(error);

      toast.error(`Failed to ${isEditMode ? "update" : "create"} entity`);
    }

    setIsSubmitting(false);
  };

  const getLabelElement = (label: string): ReactElement => {
    return (
      <Label {...(isCompact ? { color: "muted", size: "xs" } : {})}>
        {label}
      </Label>
    );
  };

  return (
    <form onSubmit={entityForm.handleSubmit(onSubmit)}>
      <div
        className={clsx(
          !isCompact ? "mt-10 grid-cols-2 gap-y-8" : "grid-cols-1 gap-y-3",
          "grid gap-x-6",
        )}
      >
        <div className="col-span-1">
          <InputFieldGroup<EntityValues>
            errors={errors}
            fieldName="name"
            label={getLabelElement("Name")}
            placeHolder="Customer Profile"
            register={register}
            rules={{
              required: "Name is required",
            }}
          />
        </div>

        <div className="col-span-1">
          <Field>
            {getLabelElement("Icon")}
            <Controller
              control={control}
              name="icon"
              render={({ field: { onChange, value } }) => (
                <IconSelectField
                  iconSource={["heroicons", "fontawesome", "custom"]}
                  onChange={(iconName) => onChange(iconName)}
                  value={value}
                />
              )}
            />
            {errors.icon && <ErrorMessage>{errors.icon.message}</ErrorMessage>}
          </Field>
        </div>

        <div className="col-span-1">
          <InputFieldGroup<EntityValues>
            disabled={isEditMode}
            errors={errors}
            fieldName="qualifiedName"
            label={getLabelElement("Entity ID")}
            placeHolder={entityIdPlaceholder}
            register={register}
            rules={{
              pattern: isEditMode
                ? undefined
                : {
                    message:
                      "Entity ID must be of the form entity.miru.entity-class.entity",
                    value: validEntityName,
                  },
              required: "Entity ID is required",
              validate: (value) => {
                const entityList = window._latestEntityList || [];
                const trimmed = value?.trim?.() ?? "";
                if (isEditMode) return true;
                return (
                  !entityList.includes(trimmed) || `${trimmed} is already used`
                );
              },
            }}
          />
        </div>

        <div className="col-span-1">
          <SelectFieldGroup<EntityValues>
            errors={errors}
            fieldName="colorSchema"
            label={getLabelElement("Color")}
            options={Object.keys(colorSchemas).map((color) => ({
              label: color,
              value: color,
            }))}
            register={register}
          />
        </div>

        <div className="col-span-1">
          <InputFieldGroup<EntityValues>
            errors={errors}
            fieldName="defaultValue"
            label={getLabelElement("Default Value (optional)")}
            placeHolder="100, true, 'N/A'"
            register={register}
          />
        </div>

        <div className="col-span-1">
          <InputFieldGroup<EntityValues>
            errors={errors}
            fieldName="description"
            label={getLabelElement("Description (optional)")}
            placeHolder="Stores user authentication data"
            register={register}
          />
        </div>

        <div className="col-span-1">
          <InputFieldGroup<EntityValues>
            errors={errors}
            fieldName="valueRegExpPattern"
            label={getLabelElement("Regular Expression Pattern (optional)")}
            placeHolder="^\\d{3}-\\d{2}-\\d{4}$"
            register={register}
          />
        </div>

        {!isCompact && (
          <div className="col-span-full">
            <Field>
              <Label>Preview</Label>
              <div className="mt-3 rounded-xl bg-black bg-dots-pattern p-3">
                <NodePreview
                  animated={false}
                  entity={{
                    colorSchema: entityForm.watch("colorSchema"),
                    defaultValue: entityForm.watch("defaultValue"),
                    entityClassId: entityClass.id,
                    icon: entityForm.watch("icon"),
                    name: entityForm.watch("name"),
                    qualifiedName: entityForm.watch("qualifiedName"),
                  }}
                />
              </div>
            </Field>
          </div>
        )}

        <FormErrorList errors={errors} serverErrors={serverErrors} />

        <div className="col-span-full">
          <Button
            color="emerald"
            disabled={isSubmitting}
            type="submit"
            {...(isCompact ? { className: "w-full mt-3", size: "xs" } : {})}
          >
            {isSubmitting ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </div>
    </form>
  );
};
